import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type { SearchResult } from '@/types/api';

/**
 * Payload for `POST /search`. Matches the server's `SearchQueryDto`.
 *
 * - `query` is the free-text search phrase — the server embeds it and
 *   hands the vector to the `embedding_search_books` RPC.
 * - `limit` / `offset` mirror the paging semantics of the plain book list
 *   endpoints. The server caps `limit` at 50 and defaults to 20 when
 *   omitted — the UI stays well under that to keep the results carousel
 *   snappy.
 */
export interface SearchQueryPayload {
  query: string;
  limit?: number;
  offset?: number;
  /**
   * Reading-level aliases of the selected reader (e.g. `["5th Grade"]`).
   * When set, the server AND-constrains results to books at these levels —
   * scoping search the same way the reader's shelves are scoped. Omitted /
   * empty = catalogue-wide search.
   */
  readingLevels?: string[];
}

export const searchService = {
  /**
   * Runs a semantic book search. The server rate-limits per account via
   * `AccountThrottlerGuard`; callers should debounce the input field so a
   * fast typist doesn't burn the tenant's OpenAI budget.
   */
  query(payload: SearchQueryPayload) {
    return apiRequest<SearchResult>(apiRoutes.search.query, {
      method: 'POST',
      body: payload,
    });
  },
};
